import type { ConfirmCardSetupParams, StripeCardDependencies } from '@frontend/react-domain';
import { addBreadcrumb } from '@sentry/react';
import { CardNumberElement } from '@stripe/react-stripe-js';
import type {
  Stripe,
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
  StripeElements,
} from '@stripe/stripe-js';

/**
 * Web-specific Stripe card state from useStripeCard hook
 */
export type WebStripeCardState = {
  cardCvcInfo?: StripeCardCvcElementChangeEvent;
  cardExpiryInfo?: StripeCardExpiryElementChangeEvent;
  cardNumberInfo?: StripeCardNumberElementChangeEvent;
  elements: StripeElements | null;
  setCardCvcInfo: (info: StripeCardCvcElementChangeEvent) => void;
  setCardExpiryInfo: (info: StripeCardExpiryElementChangeEvent) => void;
  setCardNumberInfo: (info: StripeCardNumberElementChangeEvent) => void;
  stripe: Stripe | null;
};

/**
 * Web platform implementation of Stripe card dependencies.
 *
 * Receives web-specific Stripe state via dependency injection to avoid circular imports.
 * Provides StripeCardDependencies interface for cross-platform payment step functionality.
 *
 * Uses web-specific Stripe integration:
 * - @stripe/react-stripe-js for Stripe SDK
 * - Separate card element fields (number, expiry, CVC)
 * - Web-specific error handling and Sentry integration
 *
 * @param webStripeCard - Web-specific Stripe state from useStripeCard hook
 * @returns StripeCardDependencies implementation for web platform
 */
export const useWebStripeCard = (webStripeCard: WebStripeCardState): StripeCardDependencies => {
  const {
    cardCvcInfo,
    cardExpiryInfo,
    cardNumberInfo,
    elements,
    setCardCvcInfo,
    setCardExpiryInfo,
    setCardNumberInfo,
    stripe,
  } = webStripeCard;

  // Web-specific validation: all three separate fields must be complete
  // Backend setup intent validation is handled in cross-platform layer
  const cardFieldsComplete = Boolean(cardNumberInfo?.complete && cardExpiryInfo?.complete && cardCvcInfo?.complete);

  // Web-specific card field handlers for separate elements
  const cardFieldHandlers = {
    setCardCvcInfo,
    setCardExpiryInfo,
    setCardNumberInfo,
  };

  // Web-specific Stripe confirmation implementation
  const confirmCardWithSetupIntent = async ({
    backendSetupIntent,
    client,
    onLoadingChange,
    paymentMethodId,
  }: ConfirmCardSetupParams) => {
    if (!stripe) {
      throw new Error('Stripe not ready');
    }

    if (!backendSetupIntent.stripeClientSecret) {
      throw new Error('Backend setup intent client secret is required');
    }

    // When a paymentMethodId is provided (e.g. from Apple Pay / Google Pay), use it directly.
    // Otherwise fall back to the card element.
    const paymentMethod = paymentMethodId
      ? paymentMethodId
      : (() => {
          const cardElement = elements?.getElement(CardNumberElement);
          if (!cardElement) throw new Error('Card element not ready');
          return {
            billing_details: {
              email: client.email,
              name: `${client.firstName} ${client.lastName}`,
              phone: client.phoneNumber || undefined,
            },
            card: cardElement,
          };
        })();

    onLoadingChange(true);

    try {
      // Use backend SetupIntent's stripeClientSecret with Stripe SDK
      const { error, setupIntent: confirmedSetupIntent } = await stripe.confirmCardSetup(
        backendSetupIntent.stripeClientSecret,
        {
          payment_method: paymentMethod,
        },
      );

      if (error) {
        addBreadcrumb({
          category: 'payment.confirmCardSetup',
          level: 'warning',
          message: JSON.stringify(error),
          type: 'info',
        });
        throw new Error('Failed to create setup intent with Stripe');
      }

      if (!confirmedSetupIntent) {
        throw new Error('No setup intent returned from Stripe');
      }

      return confirmedSetupIntent;
    } finally {
      onLoadingChange(false);
    }
  };

  return {
    cardFieldHandlers,
    cardFieldsComplete,
    confirmCardWithSetupIntent,
  };
};
